import {
  AppWindow,
  Bot,
  Cloud,
  Crosshair,
  Globe,
  Info,
  Lock,
} from 'lucide-react';
import React, { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useWizardFooterConfig,
  WIZARD_STEP_BODY_HEIGHT,
  WizardModalFooter,
  WizardStepBody,
} from '@/components/wizards';
import { cn } from '@/lib/utils';
import { HuntScopeStep } from '@/sections/insights/aiAssistant/components/HuntScopeStep';
import { HuntScopeTabs } from '@/sections/insights/aiAssistant/components/HuntScopeTabs';
import { AGGRESSIVENESS_OPTIONS } from '@/sections/insights/aiAssistant/types/hunt.types';
import { formatLocalDateTimeInput } from '@/sections/insights/hunts/dateTimeLocal';
import type { HuntAggressiveness } from '@/types';

export const DEFAULT_MANDATE = `Autonomously assess the in-scope environment for exploitable paths that could lead to unauthorized access, privilege escalation, or sensitive data exposure. Focus on remote code execution, authentication weaknesses, default credentials, authentication and authorization bypasses, misconfigurations leading to compromise, exposed secrets, injection attacks, and privilege-escalation validation. The focus should be on exploitability of high to critical risks. Customers require demonstration and proof to engage remediation teams.`;

export const DEFAULT_CLOUD_MANDATE = `Autonomously assess the in-scope cloud environment for security misconfigurations, privilege escalation paths, and exposure risks. Focus on IAM privilege escalation, cross-account/cross-plane trust abuse, publicly exposed services, credential and secret management weaknesses, and network segmentation gaps. Prioritize findings that enable unauthorized access, lateral movement, or sensitive data exposure. Generate leads for investigation and file verified findings with evidence.`;

export const DEFAULT_WEBAPP_MANDATE = `Autonomously assess the selected web applications for exploitable authentication, authorization, and application-layer vulnerabilities. Focus on authentication bypass,
broken access control, IDOR, injection, exposed secrets, unsafe file handling, SSRF, and meaningful privilege escalation or sensitive-data exposure. Validate findings with clear evidence suitable for remediation.`;

export const DEFAULT_LLM_MANDATE = `Autonomously assess selected LLM-backed web applications for exploitable AI and application security weaknesses. Focus on direct and indirect prompt injection, unauthorized tool execution, cross-user or cross-tenant data disclosure, retrieval isolation failures, system prompt or secret exposure, agentic workflow abuse, and sandbox escape. Validate findings with minimal, non-destructive evidence suitable for remediation.`;

// Display-only. Must stay in sync with Go hunt.DefaultGuardrails.
export const DEFAULT_GUARDRAILS = `Operate only within authorized scope. Do not perform denial-of-service, stress testing, brute force, password spraying, credential stuffing, phishing, social engineering, malware deployment, persistence, destructive actions, or bulk data exfiltration. Do not modify production data, change credentials, create accounts, alter configurations, restart services, disable security controls, clear logs, or evade monitoring. Use low-rate, low-concurrency testing. Stop or back off if testing causes errors, instability, lockout risk, throttling, or service degradation. If sensitive data is encountered, collect only minimal metadata or redacted evidence needed to prove exposure. When following redirects, stop before sending active probes, credentials, payloads, or exploit attempts to assets outside authorized scope. Passive observation of the redirect chain is permitted.`;

export const DEFAULT_FINISH_CRITERIA = `End the hunt early if any of the following occur:

1. A material critical compromise path has been validated.
2. Three consecutive iterations produce no new high-confidence findings.
3. If the agent encounters signs of service instability, account lockout risk, rate limiting, or operational impact, move on to another target.
4. If further progress would require brute force, phishing, persistence, or destructive testing, move on to another target.
5. Additional validation would materially increase risk without improving confidence.
6. The allotted duration expires.`;

export interface HannibalConfigInitial {
  name?: string;
  agent?: string;
  scope?: string[];
  mandate?: string;
  aggressiveness?: HuntAggressiveness;
}

interface LaunchHannibalDialogProps {
  open: boolean;
  mission: string;
  onLaunch: (config: {
    mandate: string;
    finishCriteria: string;
    expiresAt: string;
    agent: string;
    scope: string[];
    aggressiveness: HuntAggressiveness;
    name?: string;
  }) => void;
  onCancel: () => void;
  isPending: boolean;
  // 'profile' reuses the wizard to build a saved schedule profile instead of
  // launching: it drops the finish-criteria/duration inputs, adds a name, and
  // emits the config via onLaunch for the caller to persist.
  variant?: 'launch' | 'profile';
  initial?: HannibalConfigInitial;
}

const AGENT_TO_HUNT_TYPE: Record<string, HuntType> = {
  hannibal: 'external',
  'hannibal-cloud': 'cloud',
  'hannibal-webapp': 'webapp',
  'hannibal-llm': 'llm',
};

type StepKey = 'intro' | 'type' | 'scopeMode' | 'scope' | 'mandate' | 'finish';

const STEP_TITLES: Record<StepKey, string> = {
  intro: 'Summon Hannibal',
  type: 'Attack surface',
  scopeMode: 'Infrastructure scope',
  scope: 'Select assets',
  mandate: 'Mandate & guardrails',
  finish: 'Finish criteria & duration',
};

const HANNIBAL_LORE = `After Hannibal's invasion of Italy during the Second Punic War and following his devastating victory at the Battle of Cannae, Romans viewed the Carthaginian general as the greatest threat of their time. Roman children were taught that Hannibal grew up with a blood oath of eternal hatred toward Rome, and Roman parents would terrify their misbehaving children with the warning, "Hannibal is at the gates!"`;

type HuntType = 'external' | 'cloud' | 'webapp' | 'llm';

const HUNT_TYPES: ReadonlyArray<{
  value: HuntType;
  label: string;
  sublabel: string;
  icon: typeof Globe;
}> = [
  {
    value: 'external',
    label: 'External',
    sublabel: 'Hunts across web-facing assets for exploitable vulnerabilities.',
    icon: Globe,
  },
  {
    value: 'cloud',
    label: 'Cloud',
    sublabel:
      'Assesses cloud infrastructure for IAM, network, and service misconfigurations.',
    icon: Cloud,
  },
  {
    value: 'webapp',
    label: 'Web',
    sublabel:
      'Assesses selected web applications for authentication, authorization, and application-layer vulnerabilities.',
    icon: AppWindow,
  },
  {
    value: 'llm',
    label: 'LLM',
    sublabel:
      'Assesses selected LLM-backed applications for prompt injection, tool abuse, and data-disclosure weaknesses.',
    icon: Bot,
  },
];

// Per-surface configuration. Centralizes the agent name, default mandate, and
// scope-step target label so the render/launch paths read one map instead of
// repeating external-vs-cloud-vs-web ternaries (and so switching between any
// two surfaces correctly swaps an untouched default mandate).
const HUNT_TYPE_CONFIG: Record<
  HuntType,
  {
    agent: string;
    defaultMandate: string;
    targetLabel: string;
  }
> = {
  external: {
    agent: 'hannibal',
    defaultMandate: DEFAULT_MANDATE,
    targetLabel: 'assets',
  },
  cloud: {
    agent: 'hannibal-cloud',
    defaultMandate: DEFAULT_CLOUD_MANDATE,
    targetLabel: 'accounts',
  },
  webapp: {
    agent: 'hannibal-webapp',
    defaultMandate: DEFAULT_WEBAPP_MANDATE,
    targetLabel: 'web applications',
  },
  llm: {
    agent: 'hannibal-llm',
    defaultMandate: DEFAULT_LLM_MANDATE,
    targetLabel: 'LLM applications',
  },
};

// Surface-agnostic: the scope step now precedes the attack-surface choice, so
// the copy can't reference a specific surface. Uses the external variant's copy.
const SCOPE_MODES: ReadonlyArray<{
  value: 'all' | 'specific';
  label: string;
  sublabel: string;
  icon: typeof Globe;
}> = [
  {
    value: 'all',
    label: 'All infrastructure',
    sublabel: 'Hannibal hunts across every active target in Guard.',
    icon: Globe,
  },
  {
    value: 'specific',
    label: 'Specific infrastructure',
    sublabel: 'Choose the exact assets to scope the hunt to.',
    icon: Crosshair,
  },
];

// Pivoting posture. Ordered least → most aggressive so the segmented control
// reads left-to-right like the duration control.
function FieldLabel({
  htmlFor,
  label,
  tip,
  children,
}: {
  htmlFor: string;
  label: string;
  tip: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`About ${label}`}
            className="text-chariot-text-secondary hover:text-chariot-text-primary focus-visible:text-chariot-text-primary focus-visible:ring-chariot-brand-primary inline-flex size-5 items-center justify-center rounded-xs outline-hidden focus-visible:ring-2"
          >
            <Info className="size-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="z-[200] max-w-xs">
          {tip}
        </TooltipContent>
      </Tooltip>
      {children}
    </div>
  );
}

function LaunchHannibalForm({
  mission,
  onLaunch,
  onCancel,
  isPending,
  variant = 'launch',
  initial,
}: Omit<LaunchHannibalDialogProps, 'open'>) {
  const isProfile = variant === 'profile';
  const [huntType, setHuntType] = useState<HuntType>(
    (initial?.agent && AGENT_TO_HUNT_TYPE[initial.agent]) || 'external'
  );
  const [scopeMode, setScopeMode] = useState<'all' | 'specific'>(
    initial?.scope && initial.scope.length > 0 ? 'specific' : 'all'
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [name, setName] = useState(initial?.name ?? '');
  const [mandate, setMandate] = useState(initial?.mandate ?? mission);
  const [finishCriteria, setFinishCriteria] = useState('');
  const [durationHours, setDurationHours] = useState(24);
  const [aggressiveness, setAggressiveness] = useState<HuntAggressiveness>(
    initial?.aggressiveness ?? 'balanced'
  );
  const [scope, setScope] = useState<string[]>(initial?.scope ?? []);

  // INVARIANT: 'scope' is never the terminal step, so a false canGoNext never
  // disables the Launch button. Profile mode drops the intro (lore) and the
  // finish-criteria/duration inputs, keeping only what a saved profile needs.
  const baseSteps: StepKey[] =
    scopeMode === 'specific'
      ? ['intro', 'scopeMode', 'type', 'scope', 'mandate', 'finish']
      : ['intro', 'scopeMode', 'mandate', 'finish'];
  const steps: StepKey[] = isProfile
    ? baseSteps.filter(s => s !== 'intro')
    : baseSteps;
  const currentStep = steps[currentIndex];
  const scopedTargetLabel = HUNT_TYPE_CONFIG[huntType].targetLabel;

  // Reset scope on hunt-type change so keys from the other surface's pool can
  // never leak into the new hunt (security containment — see security-lead).
  // scopeMode is deliberately NOT reset: it's answered before the surface is
  // chosen, so switching surface must not rewind that earlier answer.
  // No currentIndex re-anchor needed: the type-step buttons only exist on the
  // specific path (where 'type' is index 2), and the other caller — the ALL-path
  // leak guard in handleScopeModeChange — has already re-anchored to 'scopeMode'.
  const handleHuntTypeChange = (type: HuntType) => {
    const prevDefault = HUNT_TYPE_CONFIG[huntType].defaultMandate;
    setHuntType(type);
    // Reset scope when switching hunt type since the target pools differ
    // (external assets vs cloud accounts vs web applications). Security
    // containment: keys from one surface's pool must never leak into another.
    setScope([]);
    // Update mandate if it still matches the previous type's default.
    const newDefault = HUNT_TYPE_CONFIG[type].defaultMandate;
    if (!mandate || mandate === prevDefault) {
      setMandate(newDefault);
    }
  };

  const handleScopeModeChange = (mode: 'all' | 'specific') => {
    setScopeMode(mode);
    // Re-anchor to the 'scopeMode' step (where this toggle lives) so the array
    // reshape (adding/removing 'scope' after it) can't strand currentIndex.
    setCurrentIndex(steps.indexOf('scopeMode'));
    if (mode === 'all') {
      // The ALL path hides the 'type' step, so a lingering non-external
      // selection (picked Specific → Cloud/Web, then Back → All) would silently
      // launch the cloud or webapp agent with no visible surface choice. Reset
      // to 'external' using handleHuntTypeChange's semantics (resets scope +
      // swaps the mandate back to DEFAULT_MANDATE when it's empty or still the
      // previous surface's default). When already external, just clear scope
      // (there are no specific targets).
      if (huntType !== 'external') {
        handleHuntTypeChange('external');
      } else {
        setScope([]);
      }
    }
  };

  const canLaunch = !isPending;

  const handleLaunch = () => {
    if (!canLaunch) return;
    const expiresAtDate = new Date();
    expiresAtDate.setHours(expiresAtDate.getHours() + durationHours);
    const expiresAt = formatLocalDateTimeInput(expiresAtDate);
    const huntConfig = HUNT_TYPE_CONFIG[huntType];
    onLaunch({
      mandate: mandate.trim() || huntConfig.defaultMandate,
      finishCriteria: finishCriteria.trim() || DEFAULT_FINISH_CRITERIA,
      expiresAt,
      agent: huntConfig.agent,
      scope: scopeMode === 'specific' ? scope : [],
      aggressiveness,
      ...(isProfile ? { name: name.trim() } : {}),
    });
  };

  const footerConfig = useWizardFooterConfig({
    canGoNext:
      (currentStep !== 'scope' || scope.length > 0) &&
      (!isProfile || currentStep !== 'mandate' || name.trim().length > 0),
    canGoBack: currentIndex > 0,
    isPending,
    isLastStep: currentIndex === steps.length - 1,
    onNext: () => setCurrentIndex(i => Math.min(i + 1, steps.length - 1)),
    onBack: () => setCurrentIndex(i => Math.max(i - 1, 0)),
    onCancel,
    onSubmit: handleLaunch,
    labels: isProfile
      ? { submit: 'Save profile', submitting: 'Saving...' }
      : { submit: 'Launch Hannibal', submitting: 'Launching...' },
  });

  return (
    <TooltipProvider>
      <DialogHeader>
        <DialogTitle>
          {isProfile ? 'Scheduled hunt profile' : 'Launch Hannibal'}
        </DialogTitle>
        <DialogDescription>
          {currentStep === 'intro'
            ? "Praetorian's autonomous offensive security agent."
            : currentStep === 'type'
              ? 'Choose the attack surfaces in scope.'
              : currentStep === 'scopeMode'
                ? 'Choose whether the hunt covers all infrastructure or specific targets.'
                : currentStep === 'scope'
                  ? `Select the ${scopedTargetLabel} to scope this hunt to.`
                  : currentStep === 'mandate'
                    ? 'Define the hunt objective and review the enforced guardrails.'
                    : isProfile
                      ? 'Choose how aggressively this profile hunts.'
                      : 'Set exit criteria and choose how long the hunt runs.'}
        </DialogDescription>
      </DialogHeader>

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5" aria-hidden="true">
          {steps.map((s, i) => (
            <span
              key={s}
              className={cn(
                'size-1.5 rounded-full transition-colors',
                i <= currentIndex
                  ? 'bg-chariot-brand-primary'
                  : 'bg-chariot-stroke'
              )}
            />
          ))}
        </div>
        <span className="text-chariot-text-secondary text-xs">
          {STEP_TITLES[currentStep]}
        </span>
        <span className="sr-only" aria-live="polite">
          Step {currentIndex + 1} of {steps.length}: {STEP_TITLES[currentStep]}
        </span>
      </div>

      {/* Every step body is WIZARD_STEP_BODY_HEIGHT so the dialog never resizes. */}
      {currentStep === 'intro' && (
        <WizardStepBody
          align="center"
          className="items-center gap-6 text-center"
        >
          <img
            src="/icons/hannibal.png"
            alt="Portrait of Hannibal, the Carthaginian general"
            className="size-56 object-contain"
          />
          <p className="text-chariot-text-secondary mx-auto max-w-md text-sm leading-relaxed">
            {HANNIBAL_LORE}
          </p>
        </WizardStepBody>
      )}

      {currentStep === 'type' && (
        <WizardStepBody align="center" className="gap-6">
          <div className="flex flex-col gap-1.5 text-center">
            <h3 className="text-chariot-text-primary text-base font-semibold">
              Attack Surface
            </h3>
            <p className="text-chariot-text-secondary text-sm">
              Choose the attack surfaces in scope
            </p>
          </div>
          <div
            role="radiogroup"
            aria-label="Attack surface"
            className="mx-auto flex w-full max-w-md flex-col gap-3"
          >
            {HUNT_TYPES.map(opt => {
              const isSelected = huntType === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => handleHuntTypeChange(opt.value)}
                  data-testid={`hunt-type-${opt.value}`}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
                    'focus-visible:ring-chariot-brand-primary outline-hidden focus-visible:ring-2',
                    isSelected
                      ? 'border-chariot-brand-primary bg-chariot-background-secondary'
                      : 'border-chariot-stroke hover:bg-chariot-background-secondary'
                  )}
                >
                  <Icon
                    className={cn(
                      'mt-0.5 size-5 shrink-0',
                      isSelected
                        ? 'text-chariot-brand-primary'
                        : 'text-chariot-text-secondary'
                    )}
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {opt.label}
                    </span>
                    <span className="text-chariot-text-secondary text-xs">
                      {opt.sublabel}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </WizardStepBody>
      )}

      {currentStep === 'scopeMode' && (
        <WizardStepBody align="center" className="gap-6">
          <div className="flex flex-col gap-1.5 text-center">
            <h3 className="text-chariot-text-primary text-base font-semibold">
              Infrastructure Scope
            </h3>
            <p className="text-chariot-text-secondary text-sm">
              Choose the scope for this hunt. You can narrow to specific assets
              next.
            </p>
          </div>
          <div
            role="radiogroup"
            aria-label="Hunt infrastructure scope"
            className="mx-auto flex w-full max-w-md flex-col gap-3"
          >
            {SCOPE_MODES.map(opt => {
              const isSelected = scopeMode === opt.value;
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => handleScopeModeChange(opt.value)}
                  data-testid={`hunt-scope-mode-${opt.value}`}
                  className={cn(
                    'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
                    'focus-visible:ring-chariot-brand-primary outline-hidden focus-visible:ring-2',
                    isSelected
                      ? 'border-chariot-brand-primary bg-chariot-background-secondary'
                      : 'border-chariot-stroke hover:bg-chariot-background-secondary'
                  )}
                >
                  <Icon
                    className={cn(
                      'mt-0.5 size-5 shrink-0',
                      isSelected
                        ? 'text-chariot-brand-primary'
                        : 'text-chariot-text-secondary'
                    )}
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {opt.label}
                    </span>
                    <span className="text-chariot-text-secondary text-xs">
                      {opt.sublabel}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </WizardStepBody>
      )}

      {currentStep === 'scope' && (
        <div className={cn('flex flex-col gap-4', WIZARD_STEP_BODY_HEIGHT)}>
          {huntType === 'external' ? (
            <HuntScopeTabs value={scope} onChange={setScope} />
          ) : (
            <>
              {/* Cloud, Web, and LLM share the untabbed layout. Mirrors
                  HuntScopeTabs' aria-live count so these paths have the same
                  "N selected" affordance as the tabbed path. */}
              <div className="flex items-center justify-end">
                <span
                  data-testid="hunt-scope-selected-count"
                  aria-live="polite"
                  className="text-chariot-text-secondary shrink-0 text-xs"
                >
                  {scope.length} selected
                </span>
              </div>
              {/* Mirror HuntScopeTabs' TabsContent wrapper (mt-0 flex min-h-0
                  flex-1 flex-col) so these paths use the same flex-fill layout
                  idiom as the tabbed path instead of relying on shrink. */}
              <div className="flex min-h-0 flex-1 flex-col">
                <HuntScopeStep
                  value={scope}
                  onChange={setScope}
                  kind={
                    huntType === 'cloud'
                      ? 'cloud'
                      : huntType === 'llm'
                        ? 'llm'
                        : 'webapplication'
                  }
                />
              </div>
            </>
          )}
        </div>
      )}

      {currentStep === 'mandate' && (
        <WizardStepBody align="top" className="gap-4">
          {isProfile && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel
                htmlFor="profile-name"
                label="Profile Name"
                tip="A label for this scheduled hunt profile."
              />
              <Input
                id="profile-name"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Weekly external sweep"
                data-testid="hunt-profile-name"
              />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <FieldLabel
              htmlFor="mandate"
              label="Hunt Mandate"
              tip="The hunt agent's primary objective. Defaults to exploitable high-to-critical risks."
            >
              {mission && (
                <span className="text-success ml-auto flex items-center gap-1 text-[11px] font-medium">
                  <span className="bg-success inline-block size-1.5 rounded-full" />
                  Pre-filled from conversation
                </span>
              )}
            </FieldLabel>
            <Textarea
              id="mandate"
              rows={7}
              value={mandate}
              onChange={e => setMandate(e.target.value)}
              placeholder={HUNT_TYPE_CONFIG[huntType].defaultMandate}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Lock
                className="text-chariot-text-secondary size-3.5"
                aria-hidden="true"
              />
              <Label>Guardrails</Label>
              <span className="text-chariot-text-secondary text-xs">
                Enforced by Praetorian
              </span>
            </div>
            <div
              className="border-chariot-stroke bg-chariot-background-secondary text-chariot-text-secondary rounded-md border p-3 text-xs leading-relaxed"
              role="region"
              aria-label="Guardrails (enforced by Praetorian)"
            >
              {DEFAULT_GUARDRAILS}
            </div>
          </div>
        </WizardStepBody>
      )}

      {currentStep === 'finish' && (
        <WizardStepBody align="top" className="gap-4">
          {!isProfile && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel
                htmlFor="finishCriteria"
                label="Finish Criteria"
                tip="Conditions that let the agent end the hunt early."
              />
              <Textarea
                id="finishCriteria"
                rows={6}
                value={finishCriteria}
                onChange={e => setFinishCriteria(e.target.value)}
                placeholder={DEFAULT_FINISH_CRITERIA}
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label>Aggressiveness</Label>
            <div
              role="radiogroup"
              aria-label="Aggressiveness"
              className="border-chariot-stroke flex overflow-hidden rounded-lg border"
            >
              {AGGRESSIVENESS_OPTIONS.map(opt => {
                const isSelected = aggressiveness === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setAggressiveness(opt.value)}
                    data-testid={`hunt-aggressiveness-${opt.value}`}
                    className={cn(
                      'border-chariot-stroke flex-1 border-r py-2 text-sm font-medium transition-colors last:border-r-0',
                      isSelected
                        ? 'bg-chariot-brand-primary text-white'
                        : 'bg-chariot-background-secondary text-chariot-text-secondary hover:bg-chariot-background-secondary/80'
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-chariot-text-secondary text-xs">
              {
                AGGRESSIVENESS_OPTIONS.find(o => o.value === aggressiveness)
                  ?.sublabel
              }
            </p>
          </div>

          {!isProfile && (
            <div className="flex flex-col gap-1.5">
              <Label>Duration</Label>
              <div className="border-chariot-stroke flex overflow-hidden rounded-lg border">
                {[8, 24, 48, 72].map(hours => (
                  <button
                    key={hours}
                    type="button"
                    onClick={() => setDurationHours(hours)}
                    className={cn(
                      'border-chariot-stroke flex flex-1 flex-col items-center justify-center gap-0.5 border-r py-3 transition-colors last:border-r-0',
                      durationHours === hours
                        ? 'bg-chariot-brand-primary text-white'
                        : 'bg-chariot-background-secondary text-chariot-text-secondary hover:bg-chariot-background-secondary/80'
                    )}
                  >
                    <span className="text-base leading-none font-semibold">
                      {hours}
                    </span>
                    <span className="text-[10px] tracking-wider uppercase opacity-75">
                      hours
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </WizardStepBody>
      )}

      <WizardModalFooter config={footerConfig} />
    </TooltipProvider>
  );
}

export function LaunchHannibalDialog({
  open,
  mission,
  onLaunch,
  onCancel,
  isPending,
  variant = 'launch',
  initial,
}: LaunchHannibalDialogProps) {
  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onCancel()}>
      <DialogContent className="max-w-3xl">
        <LaunchHannibalForm
          key={`${String(open)}-${initial?.name ?? ''}`}
          mission={mission}
          onLaunch={onLaunch}
          onCancel={onCancel}
          isPending={isPending}
          variant={variant}
          initial={initial}
        />
      </DialogContent>
    </Dialog>
  );
}
